import React from 'react';
import PropTypes from 'prop-types';
import { format, isValid, isThisYear, parseISO } from 'date-fns';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

import {
    BackgroundImage,
    Card,
    ContainerRoot,
    CardFooter,
    CardHeader,
    ContentWrapper,
    EventImage,
    EventImageContainer,
    EventTitle,
    InviteBadge,
    MetaIconBadge,
    MetaIconSvg,
    MetaItem,
    MetaRow,
    RegisterButton,
    TitleArea,
    WatermarkOverlay,
    Wrapper,
} from './AiSiteDesignerPlaceholder.styles';

const formatEventDate = (startTime) => {
    if (!startTime) return null;
    const date = parseISO(startTime);
    if (!isValid(date)) return null;
    return format(date, isThisYear(date) ? 'MMMM d' : 'MMMM d, yyyy');
};

const buildEventLocation = ({ venueName, city, state, country }) => {
    if (venueName) return venueName;
    const parts = [city, state || country].filter(Boolean);
    return parts.length ? parts.join(', ') : null;
};

const INVITE_LABEL = "You're invited";
const REGISTER_LABEL = 'Register now';

function ImagePlaceholderIcon({ size }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" />
            <circle cx="8.5" cy="8.5" r="1.5" stroke="currentColor" strokeWidth="1.5" />
            <path
                d="M21 15l-5-5L5 21"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

ImagePlaceholderIcon.propTypes = {
    size: PropTypes.string.isRequired,
};

export function AiSiteDesignerPlaceholder({
    eventTitle,
    eventStartTime,
    eventVenueName,
    eventCity,
    eventState,
    eventCountry,
    backgroundImageUrl,
    eventImageUrl,
    className,
}) {
    const [bgImageHidden, setBgImageHidden] = React.useState(false);
    const [eventImageHidden, setEventImageHidden] = React.useState(false);

    const eventDate = formatEventDate(eventStartTime);
    const eventLocation = buildEventLocation({
        venueName: eventVenueName,
        city: eventCity,
        state: eventState,
        country: eventCountry,
    });

    return (
        <ContainerRoot className={className}>
            <Wrapper aria-hidden="true">
                <WatermarkOverlay>
                    <ImagePlaceholderIcon size="3em" />
                </WatermarkOverlay>

                {backgroundImageUrl && !bgImageHidden && (
                    <BackgroundImage src={backgroundImageUrl} alt="" onError={() => setBgImageHidden(true)} />
                )}

                <ContentWrapper>
                    <Card>
                        <CardHeader>
                            <TitleArea>
                                <InviteBadge>{INVITE_LABEL}</InviteBadge>
                                <EventTitle>{eventTitle}</EventTitle>
                            </TitleArea>

                            <EventImageContainer>
                                <svg
                                    width="2.25em"
                                    height="2.25em"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    style={{ opacity: 0.25 }}
                                    aria-hidden="true"
                                >
                                    <rect
                                        x="3"
                                        y="3"
                                        width="18"
                                        height="18"
                                        rx="2"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                    />
                                    <circle cx="8.5" cy="8.5" r="1.5" stroke="currentColor" strokeWidth="1.5" />
                                    <path
                                        d="M21 15l-5-5L5 21"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                {eventImageUrl && !eventImageHidden && (
                                    <EventImage src={eventImageUrl} alt="" onError={() => setEventImageHidden(true)} />
                                )}
                            </EventImageContainer>
                        </CardHeader>

                        <CardFooter>
                            <RegisterButton>{REGISTER_LABEL}</RegisterButton>

                            <MetaRow>
                                {eventDate && (
                                    <MetaItem>
                                        <MetaIconBadge>
                                            <MetaIconSvg
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                aria-hidden="true"
                                            >
                                                <rect x="3" y="4" width="18" height="18" rx="2" />
                                                <path d="M16 2v4M8 2v4M3 10h18" />
                                            </MetaIconSvg>
                                        </MetaIconBadge>
                                        {eventDate}
                                    </MetaItem>
                                )}
                                {eventLocation && (
                                    <MetaItem>
                                        <MetaIconBadge>
                                            <MetaIconSvg
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                aria-hidden="true"
                                            >
                                                <path d="M12 21s-8-6.5-8-12a8 8 0 0116 0c0 5.5-8 12-8 12z" />
                                                <circle cx="12" cy="9" r="2.5" />
                                            </MetaIconSvg>
                                        </MetaIconBadge>
                                        {eventLocation}
                                    </MetaItem>
                                )}
                            </MetaRow>
                        </CardFooter>
                    </Card>
                </ContentWrapper>
            </Wrapper>
            <VisuallyHidden>AI-designed event page preview{eventTitle ? ` for ${eventTitle}` : ''}</VisuallyHidden>
        </ContainerRoot>
    );
}

AiSiteDesignerPlaceholder.propTypes = {
    eventTitle: PropTypes.string,
    eventStartTime: PropTypes.string,
    eventVenueName: PropTypes.string,
    eventCity: PropTypes.string,
    eventState: PropTypes.string,
    eventCountry: PropTypes.string,
    backgroundImageUrl: PropTypes.string,
    eventImageUrl: PropTypes.string,
    className: PropTypes.string,
};

AiSiteDesignerPlaceholder.defaultProps = {
    eventTitle: null,
    eventStartTime: null,
    eventVenueName: null,
    eventCity: null,
    eventState: null,
    eventCountry: null,
    backgroundImageUrl: null,
    eventImageUrl: null,
    className: undefined,
};
